<template>
  <div class="station-admin">
    <!-- ============ 顶栏 ============ -->
    <header class="app-bar">
      <div class="bar-inner">
        <div class="brand">
          <div class="logo">
            <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <path d="M24 4 41 14v20L24 44 7 34V14z" stroke="#38bdf8" stroke-width="2.4" opacity=".9" />
              <path d="M26 12 16 27h7l-2 10 11-16h-7z" fill="#38bdf8" />
            </svg>
          </div>
          <div class="brand-txt">
            <h1>工位后台管理</h1>
            <p>STATION ADMIN · 工位信息维护</p>
          </div>
        </div>

        <div class="bar-actions">


          <!-- 工位总览：按区分行列的实际排布图（行=该区最大行、列=该区最大列，格子内按位号数等分） -->
          <div ref="mapSel" class="map-sel" :class="{ open: showMap }">
            <button
              class="btn ghost map-btn"
              type="button"
              title="工位总览：查看该区行列的工位分布"
              :aria-expanded="showMap ? 'true' : 'false'"
              @click="toggleMap"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1.6" />
                <rect x="14" y="3" width="7" height="7" rx="1.6" />
                <rect x="3" y="14" width="7" height="7" rx="1.6" />
                <rect x="14" y="14" width="7" height="7" rx="1.6" />
              </svg>
              <span>总览</span>
            </button>

            <div v-show="showMap" class="map-panel">
              <div class="mp-head">
                <b class="mp-title">工位总览</b>
                <span class="mp-no">
                  {{ overviewZone ? overviewZone + ' 区 · ' : '' }}{{ overview.maxRow }} 行 ×
                  {{ overview.maxCol }} 列 · {{ overview.total }} 工位
                </span>
              </div>

              <!-- 区切换：可选区来自当前已加载数据里实际出现的区 -->
              <div class="mp-tabs">
                <button
                  v-for="z in mapZones"
                  :key="z"
                  type="button"
                  class="mp-tab"
                  :class="{ on: z === overviewZone }"
                  @click="switchZone(z)"
                >{{ z }} 区</button>
              </div>

              <!-- 批量模式：总览里也能勾选，点色块即选 / 取消，另给「选中本区 / 取消本区」 -->
              <div v-if="bulkMode" class="mp-pickbar">
                <span>批量模式：点击色块勾选</span>
                <button type="button" class="mp-pick-btn" @click="pickZone(true)">选中本区</button>
                <button type="button" class="mp-pick-btn" @click="pickZone(false)">取消本区</button>
              </div>

              <div v-if="!overview.total" class="mp-empty">该区暂无工位数据</div>
              <div v-else class="mp-body">
                <!-- 列号表头 -->
                <div class="mp-row mp-colhead">
                  <span class="mp-rno" />
                  <span v-for="c in overview.maxCol" :key="'ch' + c" class="mp-cno">{{ c }}</span>
                </div>
                <!-- 行号 + 该行每列的工位格（格子里按位号等分） -->
                <div v-for="row in overview.rows" :key="'r' + row.r" class="mp-row">
                  <span class="mp-rno">{{ row.r }}</span>
                  <div
                    v-for="cell in row.cells"
                    :key="cell.key"
                    class="mp-cell"
                    :class="{ 'is-null': !cell.slots.length }"
                  >
                    <!-- 一个工位 = 一个等分矩形色块（不显示位号数字），颜色代表状态
                         批量模式下点色块 = 勾选 / 取消勾选（面板不关，方便连选） -->
                    <button
                      v-for="st in cell.slots"
                      :key="st.id"
                      type="button"
                      class="mp-slot"
                      :class="[statusClass(st.status), { picked: bulkMode && selected.has(st.id) }]"
                      :title="slotTitle(st)"
                      @click="onMapSlotClick(st)"
                    ></button>
                    <span v-if="!cell.slots.length" class="mp-slot is-null">—</span>
                  </div>
                </div>
              </div>

              <!-- 图例：色块颜色 = 状态色 -->
              <div class="mp-foot">
                <i v-for="k in STATUS_KEYS" :key="k" :class="statusClass(k)">
                  <em class="lg-sw" />{{ k }}
                </i>
              </div>
            </div>
          </div>

          <button class="btn ghost" type="button" @click="resetAll">重置</button>
          <button class="btn ghost" type="button" title="撤销上一步 (Ctrl+Z)" @click="undo">撤销</button>
          <button class="btn primary" type="button" @click="openEdit(null)">+ 新增工位</button>
          <button
            class="btn primary"
            type="button"
            title="按「行范围 × 列范围 × 每格位号数」一次性批量生成工位"
            @click="openBatch"
          >批量新增工位</button>
          <button class="btn save" :class="{ 'need-save': isDirty }" type="button" @click="openSaveModal">保存并提交</button>
                    <!-- 未保存提示：有改动时高亮闪烁，鼠标悬浮可看到具体改了什么 -->
          <div v-show="isDirty" class="dirty-flag" :title="dirtyTip">
            <span class="df-dot" />
            <span class="df-txt">未保存修改内容</span>
          </div>

          <!-- 返回主页：仅一个小房子图标，位于"保存并提交"右侧 -->
          <button class="btn backhome" type="button" title="返回主页" @click="backHome">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M11.34 3.28a1 1 0 0 1 1.32 0l8.4 7.36a1 1 0 0 1-.66 1.75H19.4v7.4a1.4 1.4 0 0 1-1.4 1.4h-3.4v-5.6h-5.2v5.6H6a1.4 1.4 0 0 1-1.4-1.4v-7.4H3.6a1 1 0 0 1-.66-1.75z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <main class="page">
      <!-- ============ 位置预览 ============ -->
      <section class="panel pos-panel">
        <div class="pos-head">
          <h3>位置预览</h3>
          <div class="pos-legend">
            <span v-for="k in STATUS_KEYS" :key="k" class="lg" :class="statusClass(k)">{{ k }}</span>
          </div>
        </div>
        <div class="pos-map">
          <div v-if="!stations.length" class="pos-empty">暂无工位数据</div>
          <div v-for="[g, arr] in groupedStations" :key="g" class="pos-group">
            <div class="pos-gname">{{ g }} 区<i>{{ arr.length }}</i></div>
            <div class="pos-chips">
              <span
                v-for="s in arr"
                :key="s.id"
                class="pos-chip"
                :class="statusClass(s.status)"
                :title="`${s.id} · R${s.rowNo}·C${s.colNo}·P${s.posNo} · ${s.name} · ${statusText(s.status)}${s.operator ? ' · ' + s.operator : ''}`"
                @click="locateRow(s.id)"
              >{{ s.id }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ 工具栏 ============ -->
      <section class="toolbar panel">
        <div class="tools-left">
          <div class="field search">
            <svg class="ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
              <path d="m20 20-3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            <input
              v-model="kw"
              type="search"
              placeholder="搜索编号 / 区 / 名称 / 负责人 / 备注"
              autocomplete="off"
            >
          </div>
          <select v-model="fProcess" class="select" @change="loadFromServer">
            <option value="">全部区</option>
            <option v-for="p in processOptions" :key="p" :value="p">{{ p }} 区</option>
          </select>
          <select v-model="fStatus" class="select">
            <option value="">全部状态</option>
            <option v-for="st in statusOptions" :key="st" :value="st">{{ statusText(st) }}</option>
          </select>
        </div>
        <div class="tools-right">
          <span class="tip">{{ filterActive ? '筛选中：排序按当前可见列表相邻项交换' : '' }}</span>
          <button class="btn sm" :class="bulkMode ? 'primary' : 'ghost'" type="button" @click="toggleBulk">
            {{ bulkMode ? '退出批量' : '批量修改' }}
          </button>
        </div>
      </section>

      <!-- ============ 批量操作条（批量模式） ============ -->
      <section v-show="bulkMode" class="batch-bar">
        <span>已选中 <b>{{ selected.size }}</b> 项</span>
        <div class="batch-acts">
          <!-- 批量改字段：类型 / 负责人 / 备注，填（选）了哪个就改哪个，没动的不影响 -->
          <span class="ba-label">批量修改（可点击总览进行选择）</span>
          <select
            v-model="bulkType"
            class="select"
            :disabled="!selected.size"
            title="把勾选的工位统一改成这个工位类型"
          >
            <option value="">工位类型…（不改）</option>
            <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
            <option :value="BULK_CLEAR">（清空类型）</option>
          </select>
          <input
            v-model.trim="bulkOperator"
            class="ba-input"
            type="text"
            maxlength="30"
            list="dlOperator"
            autocomplete="off"
            placeholder="负责人…"
            :disabled="!selected.size || bulkClearOperator"
            title="填写后统一覆盖，留空表示不改"
          >
          <label class="ba-clr" title="勾选后把负责人清空">
            <input v-model="bulkClearOperator" type="checkbox" :disabled="!selected.size">清空
          </label>
          <input
            v-model.trim="bulkRemark"
            class="ba-input ba-remark"
            type="text"
            maxlength="200"
            placeholder="备注…"
            :disabled="!selected.size || bulkClearRemark"
            title="填写后统一覆盖，留空表示不改"
          >
          <label class="ba-clr" title="勾选后把备注清空">
            <input v-model="bulkClearRemark" type="checkbox" :disabled="!selected.size">清空
          </label>
          <button
            class="btn primary sm"
            type="button"
            :disabled="!selected.size || !bulkEditPlan.length"
            :title="bulkEditBtnTip"
            @click="applyBulkEdit"
          >应用修改</button>
          <span class="ba-sep" />
          <button class="btn danger sm" type="button" :disabled="!selected.size" @click="batchDelete">批量删除</button>
          <button class="btn ghost sm" type="button" :disabled="!selected.size" @click="clearSelection">取消选择</button>
        </div>
      </section>

      <!-- ============ 工位列表 ============ -->
      <section class="panel list-panel">
        <div class="list-body">
          <div class="list-head">
            <div class="cell c-idx">
              <input
                v-if="bulkMode"
                ref="selAll"
                type="checkbox"
                :checked="selState === 'all'"
                :indeterminate.prop="selState === 'some'"
                title="全选 / 取消全选（当前可见列表）"
                @change="toggleSelAll"
              >
              <span v-else>序号</span>
            </div>
            <div
              class="cell c-code sortable"
              :class="{ sorted: sortDir !== 0 }"
              title="点击按编号排序"
              @click="toggleSort"
            >
              编号<i class="sort-ind">{{ sortDir === 1 ? '▲' : sortDir === -1 ? '▼' : '⇅' }}</i>
            </div>
            <div class="cell c-proc">所属区</div>
            <div class="cell c-name">工位名称</div>
            <div class="cell c-type">工位类型</div>
            <div class="cell c-operator">负责人</div>
            <div class="cell c-status">状态</div>
            <div class="cell c-remark">备注</div>
            <div class="cell c-actions">操作</div>
          </div>
          <div
            v-for="(s, i) in filteredList"
            :key="s.id"
            class="row"
            :class="{ flash: lastAddedId === s.id, picked: bulkMode && selected.has(s.id) }"
            :data-id="s.id"
          >
            <div class="cell c-idx">
              <input
                v-if="bulkMode"
                type="checkbox"
                :checked="selected.has(s.id)"
                :title="'选择 ' + s.id"
                @change="toggleSel(s.id, $event)"
              >
              <span v-else class="idx">{{ i + 1 }}</span>
            </div>
            <div class="cell c-code" data-label="编号">
              {{ s.id }}
              <span v-if="isNewStation(s)" class="tag-new" title="新增行：保存提交时不含 id，由后端分配">新</span>
            </div>
            <div class="cell c-proc" data-label="所属区">{{ s.processCode || '—' }}</div>
            <div class="cell c-name" data-label="工位名称">{{ s.name }}</div>
            <div class="cell c-type" data-label="工位类型">{{ s.type || '—' }}</div>
            <div class="cell c-operator" data-label="负责人">{{ s.operator || '—' }}</div>
            <div class="cell c-status" data-label="状态">
              <span
                class="badge"
                :class="statusClass(s.status)"
                :title="s.serialCode ? `已绑定产品 ${s.serialCode}` : '未绑定产品'"
              >{{ statusText(s.status) }}</span>
            </div>
            <div class="cell c-remark" data-label="备注">{{ s.remark || '—' }}</div>
            <div class="cell c-actions">
              <button
                class="act-btn"
                :disabled="i === 0 || sortDir !== 0"
                :title="sortDir !== 0 ? '排序状态下不可移动，请先点编号列取消排序' : '上移'"
                @click="swap(s.id, -1)"
              >↑</button>
              <button
                class="act-btn"
                :disabled="i === filteredList.length - 1 || sortDir !== 0"
                :title="sortDir !== 0 ? '排序状态下不可移动，请先点编号列取消排序' : '下移'"
                @click="swap(s.id, 1)"
              >↓</button>
              <button class="act-btn" title="编辑" @click="openEdit(s.id)">✎</button>
              <button class="act-btn" title="复制一份" @click="copyStation(s.id)">⧉</button>
              <button class="act-btn del" title="删除" @click="delStation(s.id)">🗑</button>
            </div>
          </div>
        </div>
        <div v-show="filteredList.length === 0" class="empty">暂无工位数据，点击右上角「+ 新增工位」开始维护。</div>
      </section>

      <p class="foot-tip">
        编辑内容会暂存在浏览器本地草稿，需点击「保存并提交」对编辑内容进行保存。
      </p>
    </main>

    <!-- ============ 编辑 / 新增 弹窗 ============ -->
    <div v-show="editVisible" class="modal" @click.self="closeEdit">
      <div class="mask" @click="closeEdit" />
      <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="editTitle">
        <div class="dlg-head">
          <h3 id="editTitle">{{ editingId ? '编辑工位' : '新增工位' }}</h3>
          <button class="icon-btn" type="button" aria-label="关闭" @click="closeEdit">✕</button>
        </div>
        <form class="dlg-body form-grid" novalidate @submit.prevent="submitEdit">
          <!-- 工位编号：选好区 / 行 / 列 / 位后自动算出，只读展示 -->
          <div class="fi full readonly" :class="{ dup: formIdDup }">
            <span>工位编号（自动生成）</span>
            <output class="id-preview">{{ formIdPreview }}</output>
            <em class="tip">
              {{ formIdDup ? '该坐标下已有工位，请调整区 / 行 / 列 / 位' : '由所属区 . 行号 . 列号 . 位号 自动算出' }}
            </em>
          </div>
          <!-- 工位名称：按「*行*列.位号」自动算出（和批量新增 / 复制出来的命名规则完全一致） -->
          <div class="fi full readonly">
            <span>工位名称（自动生成）</span>
            <output class="id-preview">{{ formNamePreview }}</output>
            <em class="tip">由行号 / 列号 / 位号 自动算出，如 1 行 2 列 3 号 → 一行二列.3</em>
          </div>
          <label class="fi required">
            <span>所属区</span>
            <input v-model.trim="form.processCode" maxlength="4" placeholder="如 A" autocomplete="off">
          </label>
          <label class="fi required">
            <span>行号 rowNo</span>
            <input v-model.number="form.rowNo" type="number" min="1" step="1" placeholder="如 1">
          </label>
          <label class="fi required" :class="{ invalid: errors.colNo }">
            <span>列号 colNo</span>
            <input
              v-model.number="form.colNo"
              type="number" min="1" :max="MAX_COL" step="1"
              :placeholder="`1 ~ ${MAX_COL}`"
              @input="checkCoord"
            >
            <em class="err">{{ errors.colNo }}</em>
          </label>
          <label class="fi required" :class="{ invalid: errors.posNo }">
            <span>位号 posNo</span>
            <input
              v-model.number="form.posNo"
              type="number" min="1" :max="MAX_POS" step="1"
              :placeholder="`1 ~ ${MAX_POS}`"
              @input="checkCoord"
            >
            <em class="err">{{ errors.posNo }}</em>
          </label>
          <label class="fi">
            <span>工位类型</span>
            <select v-model="form.type">
              <option value="">未设置</option>
              <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
            </select>
          </label>
          <label class="fi">
            <span>负责人</span>
            <input v-model.trim="form.operator" maxlength="30" placeholder="如 张三" autocomplete="off">
          </label>
          <!-- 状态不可手改：由有没有绑产品（serial_code）决定，绑产品在另一个系统操作 -->
          <div class="fi readonly">
            <span>工位状态（自动判断）</span>
            <output class="id-preview">
              <span class="badge" :class="statusClass(formStatus)">{{ formStatus }}</span>
            </output>
            <em class="tip">有产品 → 生产中，无产品 → 空闲</em>
          </div>
          <label class="fi">
            <span>排序位置</span>
            <input v-model.number="form.order" type="number" min="1" step="1" placeholder="留空表示保持当前">
          </label>
          <label class="fi full">
            <span>备注</span>
            <textarea v-model.trim="form.remark" rows="2" maxlength="200" placeholder="选填" />
          </label>
        </form>
        <div class="dlg-foot">
          <button class="btn ghost" type="button" @click="closeEdit">取消</button>
          <button class="btn primary" type="button" @click="submitEdit">确定</button>
        </div>
      </div>
    </div>

    <!-- ============ 批量新增工位 弹窗 ============ -->
    <div v-show="batchVisible" class="modal" @click.self="closeBatch">
      <div class="mask" @click="closeBatch" />
      <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="batchTitle">
        <div class="dlg-head">
          <h3 id="batchTitle">批量新增工位</h3>
          <button class="icon-btn" type="button" aria-label="关闭" @click="closeBatch">✕</button>
        </div>
        <form class="dlg-body form-grid" novalidate @submit.prevent="submitBatch">
          <label class="fi required">
            <span>所属区</span>
            <input v-model.trim="batch.processCode" maxlength="4" placeholder="如 A" autocomplete="off">
          </label>
          <label class="fi">
            <span>工位类型</span>
            <select v-model="batch.type">
              <option value="">未设置</option>
              <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
            </select>
          </label>
          <label class="fi required">
            <span>起始行号</span>
            <input v-model.number="batch.rowFrom" type="number" min="1" max="99" step="1" placeholder="如 1">
          </label>
          <label class="fi required">
            <span>结束行号</span>
            <input v-model.number="batch.rowTo" type="number" min="1" max="99" step="1" placeholder="如 3">
          </label>
          <label class="fi required">
            <span>起始列号</span>
            <input v-model.number="batch.colFrom" type="number" min="1" :max="MAX_COL" step="1" :placeholder="`1 ~ ${MAX_COL}`">
          </label>
          <label class="fi required">
            <span>结束列号</span>
            <input v-model.number="batch.colTo" type="number" min="1" :max="MAX_COL" step="1" :placeholder="`1 ~ ${MAX_COL}`">
          </label>
          <label class="fi required">
            <span>每格位号数</span>
            <input v-model.number="batch.posCount" type="number" min="1" :max="MAX_POS" step="1" :placeholder="`1 ~ ${MAX_POS}，每个位置都生成 1~N 号`">
          </label>
          <label class="fi">
            <span>负责人</span>
            <input v-model.trim="batch.operator" maxlength="30" placeholder="选填，统一赋值" autocomplete="off" list="dlOperator">
          </label>
          <!-- 生成预览：算出总共多少个、哪些坐标已存在会被跳过 -->
          <div class="fi full readonly" :class="{ dup: !!batchPlan.msg }">
            <span>生成预览</span>
            <output class="id-preview">
              <template v-if="batchPlan.msg">{{ batchPlan.msg }}</template>
              <template v-else>
                共 {{ batchPlan.items.length }} 个 → 新增 {{ batchPlan.newCount }} 个<span
                  v-if="batchPlan.dupCount"
                >，坐标已存在跳过 {{ batchPlan.dupCount }} 个</span>
              </template>
            </output>
            <em class="tip">名称按「*行*列.位号」自动生成，如 A.01.02.03 → 一行二列.3</em>
          </div>
          <div v-if="!batchPlan.msg && batchPlan.items.length" class="fi full">
            <span>编号预览（灰=已存在将跳过）</span>
            <div class="batch-chips">
              <span
                v-for="it in batchPlan.items.slice(0, 24)"
                :key="it.id"
                class="pos-chip"
                :class="{ dup: it.dup }"
                :title="`${it.id} · ${it.name}${it.dup ? ' · 已存在，跳过' : ''}`"
              >{{ it.id }}</span>
              <span v-if="batchPlan.items.length > 24" class="pos-more">… 共 {{ batchPlan.items.length }} 个</span>
            </div>
          </div>
          <label class="fi full">
            <span>备注</span>
            <textarea v-model.trim="batch.remark" rows="2" maxlength="200" placeholder="选填，统一赋值" />
          </label>
        </form>
        <div class="dlg-foot">
          <button class="btn ghost" type="button" @click="closeBatch">取消</button>
          <button
            class="btn primary"
            type="button"
            :disabled="!!batchPlan.msg || !batchPlan.newCount"
            :title="batchPlan.msg || `生成 ${batchPlan.newCount} 个工位`"
            @click="submitBatch"
          >确定生成</button>
        </div>
      </div>
    </div>

    <!-- ============ JSON 预览弹窗 ============ -->
    <div v-show="jsonVisible" class="modal" @click.self="jsonVisible = false">
      <div class="mask" @click="jsonVisible = false" />
      <div class="dialog wide" role="dialog" aria-modal="true">
        <div class="dlg-head">
          <h3>提交预览</h3>
          <!-- <h3>提交预览 · JSON</h3> -->
          <button class="icon-btn" type="button" aria-label="关闭" @click="jsonVisible = false">✕</button>
        </div>
        <div class="dlg-body">
          <div class="diff" v-html="diffChips" />
          <pre class="json-view">{{ jsonText }}</pre>
          <p class="hint">{{ apiHint }}</p>
        </div>
        <div class="dlg-foot">
          <button class="btn ghost" type="button" @click="jsonVisible = false">关闭</button>
          <!-- <button class="btn ghost" type="button" @click="copyJson">复制 JSON</button> -->
          <button class="btn primary" type="button" @click="confirmSubmit">确认提交</button>
        </div>
      </div>
    </div>

    <!-- ============ 确认弹窗 ============ -->
    <div v-show="confirmVisible" class="modal" @click.self="confirmVisible = false">
      <div class="mask" @click="confirmVisible = false" />
      <div class="dialog narrow" role="alertdialog" aria-modal="true">
        <div class="dlg-head"><h3>{{ confirmTitle }}</h3></div>
        <div class="dlg-body"><p class="cf-text">{{ confirmText }}</p></div>
        <div class="dlg-foot">
          <button class="btn ghost" type="button" @click="confirmVisible = false">取消</button>
          <button class="btn danger" type="button" @click="runConfirm">确定</button>
        </div>
      </div>
    </div>

    <datalist id="dlOperator">
      <option v-for="o in operatorOptions" :key="o" :value="o" />
    </datalist>

    <div class="toast-wrap">
      <div v-for="t in toasts" :key="t.id" class="toast" :class="t.type">{{ t.msg }}</div>
    </div>
  </div>
</template>

<script>
import UserInfoUtils from '@/utils/userInfoUtils.js';
import { getStationList, saveStationList } from '@/api/mainapi.js';

const STORAGE_KEY = 'station-admin-draft-v1';
/**
 * 工位状态（两种）：空闲（没绑产品）/ 生产中（绑了产品）
 * 状态不由人填写，而是拿工位信息时按「有没有 serial_code」判断出来：
 *   serial_code 非空 → 生产中；为空 / 没有 → 空闲（新建工位同样是空闲）
 * 绑产品是在另一个系统里操作的，本页只做展示与维护，不提供改状态的入口。
 *   cls   —— 样式类后缀（徽章、位置预览芯片、图例、总览色块共用）
 *   color —— 总览色块填充色（空闲绿 / 生产中蓝）
 *   desc  —— 悬浮说明
 */
const STATUS_META = {
  空闲: { cls: 'st-idle', color: '#22c55e', desc: '就绪待命' },
  生产中: { cls: 'st-running', color: '#3b82f6', desc: '正在生产' },
  // 故障: { cls: 'st-fault', color: '#ef4444', desc: '异常停机' },
  // 维修中: { cls: 'st-maintenance', color: '#f97316', desc: '维修 / 保养中' },
  // 离线: { cls: 'st-offline', color: '#475569', desc: '禁用 / 下线' },
};
const STATUS_KEYS = Object.keys(STATUS_META); // 顺序 = 下拉 / 图例顺序
/**
 * 兼容表：历史英文枚举、本地旧草稿 → 两种标准状态
 * 注：现在状态由 serial_code 推导，这张表只用于兜底（草稿 / 老数据里的乱值）；
 *     认不出来的值原样显示（st-unknown），不会凭空造状态
 */
const STATUS_ALIAS = {
  // 英文枚举
  idle: '空闲', running: '生产中', fault: '故障', error: '故障',
  maintenance: '维修中', offline: '离线', enabled: '生产中', disabled: '离线',
  // 旧中文叫法 / 老草稿
  运行: '生产中', 维修: '维修中', 停用: '离线', 启用: '生产中', 异常: '故障',
  待机: '空闲', 待产: '空闲', 停机: '离线', 暂停: '离线',
  // 常见同义叫法
  在线: '生产中', 工作中: '生产中', 加工中: '生产中', 检修: '维修中', 保养: '维修中',
};
const MAX_COL = 4; // 列号 colNo 上限
const MAX_POS = 6; // 位号 posNo 上限
const TYPE_OPTIONS = ['装配', '测试', '维修', '暂存'];
const MAX_BATCH = 200; // 单次批量生成上限，防止手抖填出上千个工位
const BULK_CLEAR = '__clear__'; // 批量修改里「（清空）」选项的哨兵值（不能和真实取值撞上）
const clone = v => JSON.parse(JSON.stringify(v));
/** 数字转中文：工位名称用「一行二列」这种写法，1 ~ 99 足够 */
function cnNum(n) {
  const D = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  const v = Number(n) || 0;
  if (v <= 0) return String(v);
  if (v < 10) return D[v];
  if (v < 20) return `十${v % 10 ? D[v % 10] : ''}`;
  const t = Math.floor(v / 10);
  const o = v % 10;
  return `${D[t]}十${o ? D[o] : ''}`;
}
/** 工位名称统一按编号自动生成：「*行*列.位号」，如 1 行 2 列 3 号 → 一行二列.3
    新增工位 / 批量新增 / 表格里复制，三处都走这个函数，命名口径永远和编号一致 */
const stationNameOf = (rowNo, colNo, posNo) =>
  `${cnNum(rowNo)}行${cnNum(colNo)}列.${Number(posNo) || 0}`;
/**
 * 取第一个有值的字段：新接口是下划线命名（station_name…），
 * 旧接口 / 本地草稿是小驼峰（name…），两种都要认，避免后端回滚或草稿旧数据解析不出来
 */
const pickVal = (...vals) => {
  for (let i = 0; i < vals.length; i += 1) {
    const v = vals[i];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
};

export default {
  name: 'StationAdmin',
  data() {
    return {
      STATUS_META,
      STATUS_KEYS,
      TYPE_OPTIONS,
      MAX_COL,
      MAX_POS,

      stations: [],
      baseline: [],
      // 出现过的区（processCode）全集：区筛选是按区重新拉数据的，
      // 若选项只从当前 stations 派生，选了 A 之后 B 就会从下拉里消失
      processBase: [],
      history: [],
      selected: new Set(),
      editingId: null,
      lastAddedId: null,

      kw: '',
      fProcess: '',
      fGroup: '',
      fStatus: '',
      // 批量模式：开启后序号列变多选框，并显示批量操作条
      bulkMode: false,
      BULK_CLEAR, // 模板里「（清空）」选项用的哨兵值
      // 批量改字段：填（选）了哪个就改哪个，留空 / 未选的不动
      bulkType: '',
      bulkOperator: '',
      bulkRemark: '',
      bulkClearOperator: false,
      bulkClearRemark: false,
      // 编号列排序：0 = 原始顺序，1 = 升序，-1 = 降序
      sortDir: 0,

      editVisible: false,
      form: this.emptyForm(),
      errors: { colNo: '', posNo: '' }, // 行内校验提示：名称已自动生成，只校验列号 / 位号

      // 批量新增工位弹窗：区 + 行范围 × 列范围 × 每格位号数，一次生成一片工位
      batchVisible: false,
      batch: this.emptyBatch(),

      jsonVisible: false,
      jsonText: '',

      confirmVisible: false,
      confirmTitle: '请确认',
      confirmText: '',
      confirmFn: null,

      toasts: [],
      toastId: 0,

      // 右上角工位总览面板：是否展开 + 当前查看的区
      showMap: false,
      mapZone: '',
    };
  },
  computed: {
    filteredList() {
      const kw = this.kw.trim().toLowerCase();
      let arr = this.stations.filter(s => {
        if (this.fProcess && s.processCode !== this.fProcess) return false;
        if (this.fGroup && s.group !== this.fGroup) return false;
        if (this.fStatus && this.statusOf(s.status) !== this.fStatus) return false;
        if (!kw) return true;
        return [
          s.id, s.processCode,
          String(s.rowNo || ''), String(s.colNo || ''), String(s.posNo || ''),
          s.name, s.group, s.type, s.operator, s.remark,
        ].join(' ').toLowerCase().includes(kw);
      });
      // 编号列排序（A.01.01.02 这类编号按数字段比较，纯字符串排序会把 10 排在 2 前面）
      if (this.sortDir !== 0) {
        const dir = this.sortDir;
        arr = arr.slice().sort((a, b) =>
          String(a.id).localeCompare(String(b.id), 'zh-CN', { numeric: true }) * dir
        );
      }
      return arr;
    },
    filterActive() {
      return !!(this.kw.trim() || this.fProcess || this.fGroup || this.fStatus);
    },
    // 可选 processCode（A/B/C…）：取累积的区全集 + 当前列表，按字母排序
    processOptions() {
      return [...new Set([...this.processBase, ...this.stations.map(s => s.processCode)].filter(Boolean))].sort();
    },
    // 可选状态从实际数据派生（后端返回的是「生产中」这类中文状态，不再写死英文枚举）
    // 未知状态也保留，避免数据里的历史值在下拉里消失
    statusOptions() {
      const seen = new Set(this.stations.map(s => this.statusOf(s.status)).filter(Boolean));
      const known = STATUS_KEYS.filter(k => seen.has(k));
      const other = [...seen].filter(k => !STATUS_KEYS.includes(k)).sort();
      return [...known, ...other];
    },
    operatorOptions() {
      return [...new Set(this.stations.map(s => s.operator).filter(Boolean))].sort();
    },
    typeOptions() {
      return [...new Set([...TYPE_OPTIONS, ...this.stations.map(s => s.type).filter(Boolean)])];
    },
    /* ---------------- 弹窗：自动算出的工位编号 ---------------- */
    // 选好区 / 行 / 列 / 位后实时算出：B + 02 + 01 + 03 → B.02.01.03
    formIdPreview() {
      return this.deriveId({
        processCode: this.form.processCode,
        rowNo: this.form.rowNo,
        colNo: this.form.colNo,
        posNo: this.form.posNo,
      });
    },
    // 工位名称：与编号同源，按「*行*列.位号」实时算出（改行 / 列 / 位，名称跟着变）
    formNamePreview() {
      return stationNameOf(this.form.rowNo, this.form.colNo, this.form.posNo);
    },
    // 算出来的编号是否已存在（编辑时排除自己）：存在则标红提示，不用等点确定
    formIdDup() {
      return this.stations.some(s => s.id === this.formIdPreview && s.id !== this.editingId);
    },
    /* ---------------- 批量新增工位：按行范围 × 列范围 × 每格位号数算出要生成什么 ---------------- */
    // 返回 { msg, items, newCount, dupCount }：msg 非空表示条件不合法，不能生成
    batchPlan() {
      const b = this.batch;
      const rowFrom = Number(b.rowFrom) || 0;
      const rowTo = Number(b.rowTo) || 0;
      const colFrom = Number(b.colFrom) || 0;
      const colTo = Number(b.colTo) || 0;
      const posCount = Number(b.posCount) || 0;
      const bad = msg => ({ msg, items: [], newCount: 0, dupCount: 0 });
      if (!b.processCode) return bad('请填写所属区');
      if (!rowFrom || !rowTo || !colFrom || !colTo || !posCount) return bad('行号 / 列号 / 位号数 均需填写（大于 0）');
      if (rowTo < rowFrom) return bad('结束行号不能小于起始行号');
      if (colTo < colFrom) return bad('结束列号不能小于起始列号');
      if (rowTo > 99) return bad('行号不得超过 99');
      if (colTo > MAX_COL) return bad(`列号不得超过 ${MAX_COL}`);
      if (posCount > MAX_POS) return bad(`每格位号数不得超过 ${MAX_POS}`);
      const total = (rowTo - rowFrom + 1) * (colTo - colFrom + 1) * posCount;
      if (total > MAX_BATCH) return bad(`一次最多生成 ${MAX_BATCH} 个，当前为 ${total} 个，请缩小范围`);

      // 同区下已占用的坐标：撞上的不重复生成，直接跳过
      const used = new Set(
        this.stations.filter(s => s.processCode === b.processCode)
          .map(s => `${s.rowNo}|${s.colNo}|${s.posNo}`)
      );
      const items = [];
      for (let r = rowFrom; r <= rowTo; r += 1) {
        for (let c = colFrom; c <= colTo; c += 1) {
          for (let p = 1; p <= posCount; p += 1) {
            items.push({
              rowNo: r,
              colNo: c,
              posNo: p,
              dup: used.has(`${r}|${c}|${p}`),
              id: this.deriveId({ processCode: b.processCode, rowNo: r, colNo: c, posNo: p }),
              name: stationNameOf(r, c, p),
            });
          }
        }
      }
      const dupCount = items.filter(it => it.dup).length;
      return { msg: '', items, newCount: items.length - dupCount, dupCount };
    },
    /* ---------------- 批量改字段：把「填了什么」翻译成要改的字段列表 ---------------- */
    // 每项 { key, label, value, txt }：value 是最终写进工位的值，txt 只用于提示 / 确认文案
    bulkEditPlan() {
      const f = [];
      if (this.bulkType) {
        f.push({
          key: 'type',
          label: '工位类型',
          value: this.bulkType === BULK_CLEAR ? '' : this.bulkType,
          txt: this.bulkType === BULK_CLEAR ? '（清空）' : this.bulkType,
        });
      }
      if (this.bulkClearOperator) f.push({ key: 'operator', label: '负责人', value: '', txt: '（清空）' });
      else if (this.bulkOperator) f.push({ key: 'operator', label: '负责人', value: this.bulkOperator, txt: this.bulkOperator });
      if (this.bulkClearRemark) f.push({ key: 'remark', label: '备注', value: '', txt: '（清空）' });
      else if (this.bulkRemark) f.push({ key: 'remark', label: '备注', value: this.bulkRemark, txt: this.bulkRemark });
      return f;
    },
    // 按钮悬浮提示：说清楚会把哪几项改成什么
    bulkEditBtnTip() {
      const f = this.bulkEditPlan;
      if (!this.selected.size) return '先勾选要修改的工位';
      if (!f.length) return '先选择 / 填写要修改的内容';
      return `把选中的 ${this.selected.size} 个工位：${f.map(x => `${x.label} → ${x.txt}`).join('，')}`;
    },
    // 弹窗里的工位状态：只读，按当前工位有没有产品编号判断（新增工位必然是空闲）
    formStatus() {
      const s = this.editingId ? this.stations.find(x => x.id === this.editingId) : null;
      return s && s.serialCode ? '生产中' : STATUS_KEYS[0];
    },
    // 全选（用 getter/setter 绑定 indeterminate 语义）
    selArr: {
      get() { return this.filteredList.filter(s => this.selected.has(s.id)).map(s => s.id); },
      set(val) {
        if (val.length === 0) {
          this.filteredList.forEach(s => this.selected.delete(s.id));
        } else {
          val.forEach(id => this.selected.add(id));
        }
      },
    },
    selAll: {
      get() {
        const vis = this.filteredList;
        if (!vis.length) return false;
        const n = vis.filter(s => this.selected.has(s.id)).length;
        return n === vis.length;
      },
      set(val) {
        this.filteredList.forEach(s => {
          if (val) this.selected.add(s.id);
          else this.selected.delete(s.id);
        });
      },
    },
    // 全选半选状态：'all' / 'some' / 'none'
    selState() {
      const vis = this.filteredList;
      if (!vis.length) return 'none';
      const n = vis.filter(s => this.selected.has(s.id)).length;
      if (n === 0) return 'none';
      if (n === vis.length) return 'all';
      return 'some';
    },
    isDirty() {
      return JSON.stringify(this.stations) !== JSON.stringify(this.baseline);
    },
    // 未保存提示的悬浮说明：告诉用户到底改了什么
    dirtyTip() {
      const d = this.diffSummary();
      const parts = [];
      if (d.added) parts.push(`新增 ${d.added}`);
      if (d.updated) parts.push(`修改 ${d.updated}`);
      if (d.removed) parts.push(`删除 ${d.removed}`);
      if (d.moved) parts.push('顺序调整');
      return parts.length
        ? `未保存修改内容：${parts.join(' · ')}，请点击「保存并提交」`
        : '未保存修改内容，请点击「保存并提交」';
    },
    groupedStations() {
      // 按 processCode（A/B/C…）顶层分组；同组内按 (rowNo, colNo, posNo) 排序，便于一眼看出位置
      const map = new Map();
      this.stations.forEach(s => {
        const g = s.processCode || '未分区';
        if (!map.has(g)) map.set(g, []);
        map.get(g).push(s);
      });
      return [...map.entries()].map(([g, arr]) => {
        arr.sort((a, b) =>
          (a.rowNo - b.rowNo) || (a.colNo - b.colNo) || (a.posNo - b.posNo)
        );
        return [g, arr];
      });
    },
    /* ---------------- 工位总览（右上角面板） ---------------- */
    // 可选区：当前数据里实际出现的区（A/B/C…，未分区）
    mapZones() {
      return this.groupedStations.map(([g]) => g);
    },
    // 当前总览展示的区：选中的区若已从数据中消失（切换区筛选 / 重新拉取），自动回落到第一个区
    overviewZone() {
      const zs = this.mapZones;
      if (!zs.length) return '';
      return zs.indexOf(this.mapZone) >= 0 ? this.mapZone : zs[0];
    },
    /**
     * 总览矩阵：行数 = 该区最大行号、列数 = 该区最大列号（缺坐标的格子留空，位置关系才准确）
     * 每个格子里的工位按位号从小到大排列，并用 flex 等分（6 个工位 → 6 等分，2 个 → 2 等分）
     */
    overview() {
      const list = this.stations.filter(s => (s.processCode || '未分区') === this.overviewZone);
      const maxOf = key => list.reduce((m, s) => Math.max(m, Number(s[key]) || 0), 0);
      const maxRow = maxOf('rowNo');
      const maxCol = maxOf('colNo');

      // 同一 (行,列) 坐标上的工位归到一起
      const at = new Map();
      list.forEach(s => {
        const k = `${Number(s.rowNo) || 0}-${Number(s.colNo) || 0}`;
        if (!at.has(k)) at.set(k, []);
        at.get(k).push(s);
      });

      const rows = [];
      for (let r = 1; r <= maxRow; r += 1) {
        const cells = [];
        for (let c = 1; c <= maxCol; c += 1) {
          const k = `${r}-${c}`;
          const slots = (at.get(k) || []).slice()
            .sort((a, b) => (Number(a.posNo) || 0) - (Number(b.posNo) || 0));
          cells.push({ key: k, slots });
        }
        rows.push({ r, cells });
      }
      return { maxRow, maxCol, rows, total: list.length };
    },
    diffChips() {
      const d = this.diffSummary();
      const items = [
        d.added ? `<span class="add">新增 ${d.added}</span>` : '',
        d.updated ? `<span class="upd">修改 ${d.updated}</span>` : '',
        d.removed ? `<span class="del">删除 ${d.removed}</span>` : '',
        d.moved ? `<span class="mov">顺序调整</span>` : '',
      ].filter(Boolean).join('');
      return items || '<span>与上次保存相比无变化</span>';
    },
    apiHint() {
      // return '提交时将 POST 到 api/dash/cockpit/station（params.Type=WorkStationSave）。Body 为 { stations: [...] }，每个 station 含 processCode/rowNo/colNo/posNo/order/name/group/type/operator/status/remark。新增行不含 id，由后端分配；已有行带 id 供后端更新。提交成功后会自动重新拉取服务器最新数据。';
      return '提交成功后会自动重新拉取服务器最新数据。';
    },
  },
  watch: {
    // 全选/半选状态变化时同步 checkbox 的 indeterminate
    selState() {
      this.$nextTick(() => {
        if (this.$refs.selAll) this.$refs.selAll.indeterminate = this.selState === 'some';
      });
    },
  },
  mounted() {
    this.loadFromServer();
    window.addEventListener('keydown', this.onKeydown);
    // 点击面板外部关闭工位总览（面板内部的点击由 contains 判断放行）
    document.addEventListener('click', this.onDocClick);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    emptyForm() {
      return {
        processCode: 'A', rowNo: 1, colNo: 1, posNo: 1,
        name: '', group: '', type: '', operator: '',
        status: STATUS_KEYS[0], order: '', remark: '', // 默认取状态表里的第一个（待机）
      };
    },
    // 批量新增工位表单：默认「1~1 行 × 1~1 列 × 1 个位号」，区沿用当前筛选
    emptyBatch() {
      return {
        processCode: '', rowFrom: 1, rowTo: 1, colFrom: 1, colTo: 1, posCount: 1,
        type: '', operator: '', remark: '',
      };
    },
    // 派生工位 id：`{processCode}.{rowNo:02}.{colNo:02}.{posNo:02}`，后端约定格式
    deriveId(s) {
      const p = s.processCode || 'X';
      const r = String(Number(s.rowNo) || 0).padStart(2, '0');
      const c = String(Number(s.colNo) || 0).padStart(2, '0');
      const po = String(Number(s.posNo) || 0).padStart(2, '0');
      return `${p}.${r}.${c}.${po}`;
    },

    /* ---------------- 数据加载（真实接口） ---------------- */
    // 从后端拉取工位列表；失败时回退到本地草稿，均无则显示空列表
    async loadFromServer() {
      try {
        const res = await getStationList({ processCode: this.fProcess || '' });
        const body = res.data || {};
        if (body.code !== 200) throw new Error(body.message || '返回数据格式异常');
        const list = Array.isArray(body.data) ? body.data : [];
        this.stations = list.map(s => this.normalizeStation(s));
        this.collectProcess(this.stations);
        this.baseline = clone(this.stations);
        this.selected = new Set();
        this.history = [];
        this.clearDraft();
        this.toast('已加载工位数据：共 ' + this.stations.length + ' 条', 'ok');
        return true;
      } catch (e) {
        if (this.loadDraft()) {
          this.toast('加载服务器数据失败，已显示本地草稿', 'warn');
          return false;
        }
        this.stations = [];
        this.baseline = [];
        this.toast('加载工位数据失败：' + (e.message || '网络错误'), 'err');
        return false;
      }
    },
    // 累积记录出现过的区：区下拉始终保留所有已知区，不随当前筛选缩水
    collectProcess(list) {
      const codes = (list || []).map(s => s.processCode).filter(Boolean);
      this.processBase = [...new Set([...this.processBase, ...codes])];
    },
    /* ---------------- 状态归一 ---------------- */
    // 任意写法（中文 / 英文枚举 / 历史值）→ 六种标准状态之一；认不出来的原样返回
    statusOf(v) {
      const raw = String(v == null ? '' : v).trim();
      if (STATUS_META[raw]) return raw;
      return STATUS_ALIAS[raw] || STATUS_ALIAS[raw.toLowerCase()] || raw;
    },
    // 显示文案：标准状态直接显示，未知状态显示原始值
    statusText(v) {
      const k = this.statusOf(v);
      return STATUS_META[k] ? k : (k || '—');
    },
    // 样式类：st-idle / st-running / st-fault / st-maintenance / st-offline / st-unknown
    statusClass(v) {
      const k = this.statusOf(v);
      return STATUS_META[k] ? STATUS_META[k].cls : 'st-unknown';
    },
    /**
     * 后端行 → 前端行：新接口字段为下划线命名，映射关系是
     *   station_id → id        工位编号（B.02.01.03）
     *   station_name → name    工位名称
     *   station_remark → remark
     *   process_code → processCode
     *   station_type → type
     *   row_no / col_no / pos_no → rowNo / colNo / posNo
     *   operator → operator
     *   serial_code → serialCode 有值即视为已绑产品（status = 生产中）
     *   station_status 只作兜底：状态优先按 serial_code 判断，因为后端返回的
     *                  station_status 对未绑产品的工位也可能写成「生产中」
     *   group 后端不再使用，直接忽略（前端恒为空）
     *   产品相关字段（product_name / model / task_number …）本页不用，不落库
     */
    normalizeStation(s) {
      s = s || {};
      const rowNo = Number(pickVal(s.row_no, s.rowNo)) || 0;
      const colNo = Number(pickVal(s.col_no, s.colNo)) || 0;
      const posNo = Number(pickVal(s.pos_no, s.posNo)) || 0;
      const serialCode = pickVal(s.serial_code, s.serialCode); // 产品编号：有没有绑产品就看它
      const obj = {
        processCode: pickVal(s.process_code, s.processCode),
        rowNo,
        colNo,
        posNo,
        name: pickVal(s.station_name, s.name),
        group: '', // 后端 group 字段不用，直接忽略
        type: pickVal(s.station_type, s.type),
        operator: pickVal(s.operator),
        serialCode,
        // 状态 = 有没有绑产品：serial_code 有值 → 生产中，否则空闲
        // 不用后端 station_status：它对没绑产品的工位也会返回「生产中」，以 serial_code 为准
        status: serialCode ? '生产中' : STATUS_KEYS[0],
        remark: pickVal(s.station_remark, s.remark),
        order: Number(s.order) || 0,
      };
      // 编号优先用后端 station_id；接口没带时按「区.行.列.位」兜底算出
      obj.id = pickVal(s.station_id, s.id) || this.deriveId(obj);
      return obj;
    },

    /* ---------------- 草稿 ---------------- */
    loadDraft() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const data = JSON.parse(raw);
          if (data && Array.isArray(data.stations)) {
            this.stations = data.stations.map(s => this.normalizeStation(s));
            this.collectProcess(this.stations);
            this.baseline = Array.isArray(data.baseline)
              ? data.baseline.map(s => this.normalizeStation(s))
              : clone(this.stations);
            return true;
          }
        }
      } catch (e) { /* ignore */ }
      return false;
    },
    saveDraft() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ stations: this.stations, baseline: this.baseline }));
      } catch (e) { /* ignore */ }
    },
    clearDraft() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    },

    /* ---------------- 变更追踪 / 撤销 ---------------- */
    pushHistory() {
      this.history.push(clone(this.stations));
      if (this.history.length > 40) this.history.shift();
    },
    undo() {
      if (!this.history.length) return this.toast('没有可撤销的操作', 'warn');
      this.stations = this.history.pop();
      this.selected = new Set();
      this.persist();
      this.toast('已撤销上一步', 'ok');
    },
    diffSummary() {
      const cur = this.stations, base = this.baseline;
      const baseMap = new Map(base.map(s => [s.id, s]));
      const curMap = new Map(cur.map(s => [s.id, s]));
      let added = 0, updated = 0, removed = 0;
      cur.forEach(s => {
        const b = baseMap.get(s.id);
        if (!b) { added++; return; }
        if (JSON.stringify(this.stripOrder(s)) !== JSON.stringify(this.stripOrder(b))) updated++;
      });
      base.forEach(s => { if (!curMap.has(s.id)) removed++; });
      const baseOrder = base.filter(s => curMap.has(s.id)).map(s => s.id).join('|');
      const curOrder = cur.filter(s => baseMap.has(s.id)).map(s => s.id).join('|');
      return { added, updated, removed, moved: baseOrder !== curOrder };
    },
    stripOrder(s) {
      return {
        id: s.id, processCode: s.processCode, rowNo: s.rowNo, colNo: s.colNo, posNo: s.posNo,
        name: s.name, group: s.group, type: s.type, operator: s.operator, status: s.status, remark: s.remark,
      };
    },
    isNewStation(s) {
      return !this.baseline.some(b => b.id === s.id);
    },

    /* ---------------- 数据操作 ---------------- */
    persist() {
      this.lastAddedId = null;
      // 整体替换 Set 引用：Vue2 侦测不到 Set.add/delete，替换才能刷新勾选状态
      const next = new Set(this.selected);
      const alive = new Set(this.stations.map(s => s.id));
      [...next].forEach(id => { if (!alive.has(id)) next.delete(id); });
      this.selected = next;
      this.saveDraft();
    },
    swap(id, dir) {
      const visIds = new Set(this.filteredList.map(s => s.id));
      const ids = this.stations.map(s => s.id);
      const i = ids.indexOf(id);
      if (i < 0) return;
      for (let k = i + dir; k >= 0 && k < ids.length; k += dir) {
        if (visIds.has(ids[k])) {
          this.pushHistory();
          // Vue2 中直接索引赋值不触发视图更新，必须用 $set（数据其实已换，只是列表没刷新）
          const t = this.stations[i];
          this.$set(this.stations, i, this.stations[k]);
          this.$set(this.stations, k, t);
          this.persist();
          return;
        }
      }
      this.toast(dir < 0 ? '已在最顶部' : '已在最底部', 'warn');
    },
    removeStations(ids) {
      this.pushHistory();
      this.stations = this.stations.filter(s => !ids.includes(s.id));
      const next = new Set(this.selected);
      ids.forEach(id => next.delete(id));
      this.selected = next;
      this.persist();
    },
    moveToPosition(id, pos) {
      const i = this.stations.findIndex(s => s.id === id);
      if (i < 0) return;
      const j = Math.max(0, Math.min(this.stations.length - 1, pos - 1));
      if (i === j) return;
      this.pushHistory();
      const [item] = this.stations.splice(i, 1);
      this.stations.splice(j, 0, item);
    },
    copyStation(id) {
      const i = this.stations.findIndex(s => s.id === id);
      if (i < 0) return;
      const src = this.stations[i];
      const used = new Set(this.stations.map(s => `${s.processCode}|${s.rowNo}|${s.colNo}|${s.posNo}`));
      // 只在 1 ~ MAX_POS 里从小到大找空位（源位号本身已被占用会自动跳过），找不到就提示失败
      let p = null;
      for (let n = 1; n <= MAX_POS; n++) {
        if (!used.has(`${src.processCode}|${src.rowNo}|${src.colNo}|${n}`)) { p = n; break; }
      }
      if (p === null) {
        this.toast(`复制失败：该位置位号已占满，位号不得超过 ${MAX_POS}`, 'err');
        return;
      }
      this.pushHistory();
      // 只搬工位本身的属性：station_remark / process_code / station_type /
      // operator / row_no / col_no / pos_no；产品绑定信息（serial_code、product_* 等）一律不带，
      // 所以复制出来的新工位 serialCode 为空、状态自动是「空闲」，编号 / 名称都按新坐标重新算
      const copy = this.normalizeStation({
        // 名称也自动：按「*行*列.位号」跟着新位号走（不再沿用源工位的名称加序号）
        station_name: stationNameOf(src.rowNo, src.colNo, p),
        station_remark: src.remark,
        process_code: src.processCode,
        station_type: src.type,
        operator: src.operator,
        row_no: src.rowNo,
        col_no: src.colNo,
        pos_no: p, // 复制品视作新增行：另找一个不冲突的位号，id 由坐标重新算出（后端分配）
      });
      this.stations.splice(i + 1, 0, copy);
      this.lastAddedId = copy.id;
      this.toast(`已复制为 ${copy.id}（${copy.name}）`, 'ok');
      this.persist();
    },
    delStation(id) {
      const s = this.stations.find(x => x.id === id);
      if (!s) return;
      this.askConfirm(`确定删除工位「${s.id} ${s.name}」？`, () => {
        this.removeStations([id]);
        this.toast('已删除 ' + s.id, 'ok');
      }, '删除确认');
    },
    /* ---------------- 批量模式 ---------------- */
    toggleBulk() {
      this.bulkMode = !this.bulkMode;
      if (!this.bulkMode) { this.clearSelection(); this.resetBulkEdit(); }
      else this.toast('已进入批量模式：勾选工位后可批量改 类型 / 负责人 / 备注，或批量删除', 'ok');
    },
    // 单行勾选：整体替换 Set 引用，Vue2 才能感知变化
    toggleSel(id, e) {
      const next = new Set(this.selected);
      if (e.target.checked) next.add(id); else next.delete(id);
      this.selected = next;
    },
    // 表头全选：只作用于当前可见（已筛选/已排序）的列表
    toggleSelAll(e) {
      const checked = e.target.checked;
      const next = new Set(this.selected);
      this.filteredList.forEach(s => {
        if (checked) next.add(s.id);
        else next.delete(s.id);
      });
      this.selected = next;
    },
    // 点击「编号」列标题：升序 → 降序 → 恢复原始顺序
    toggleSort() {
      this.sortDir = this.sortDir === 0 ? 1 : (this.sortDir === 1 ? -1 : 0);
      const txt = this.sortDir === 1 ? '编号升序' : this.sortDir === -1 ? '编号降序' : '恢复默认顺序';
      this.toast('已按' + txt + '排列', 'ok');
    },
    /* ---------------- 批量改「工位类型 / 负责人 / 备注」---------------- */
    // 先弹确认框说清楚改哪几项，确认后统一写入，整批算一步可撤销
    applyBulkEdit() {
      const fields = this.bulkEditPlan;
      const ids = [...this.selected];
      if (!ids.length) { this.toast('请先勾选要修改的工位', 'err'); return; }
      if (!fields.length) { this.toast('请先选择 / 填写要修改的内容', 'err'); return; }
      const detail = fields.map(f => `${f.label} → ${f.txt}`).join('，');
      this.askConfirm(
        `确定把选中的 ${ids.length} 个工位的 ${detail}？`,
        () => this.batchSetFields(fields),
        '批量修改',
      );
    },
    batchSetFields(fields) {
      const ids = [...this.selected];
      if (!ids.length) return;
      this.pushHistory(); // 整批算一步，撤销一次全部回退
      let changed = 0;
      this.stations.forEach((s) => {
        if (!ids.includes(s.id)) return;
        let hit = false;
        fields.forEach((f) => {
          if (s[f.key] === f.value) return; // 已经是目标值的跳过，不计入改动
          s[f.key] = f.value;
          hit = true;
        });
        if (hit) changed += 1;
      });
      this.toast(
        changed ? `已修改 ${changed} 个工位` : '选中的工位已经是目标内容，无需修改',
        changed ? 'ok' : 'warn',
      );
      this.resetBulkEdit(); // 改完清空输入，避免下次误触
      this.persist();
    },
    // 清空批量修改的三个输入（含两个「清空」勾选）
    resetBulkEdit() {
      this.bulkType = '';
      this.bulkOperator = '';
      this.bulkRemark = '';
      this.bulkClearOperator = false;
      this.bulkClearRemark = false;
    },
    // 批量改状态：先弹确认框（含「已是该状态的数量」提示），确认后统一改
    applyBulkStatus() {
      const status = this.bulkStatus;
      if (!status) { this.toast('请先选择要改为的状态', 'err'); return; }
      const ids = [...this.selected];
      if (!ids.length) { this.toast('请先勾选要修改的工位', 'err'); return; }
      const map = new Map(this.stations.map(s => [s.id, s]));
      const already = ids.filter(id => this.statusOf((map.get(id) || {}).status) === status).length;
      const tip = already ? `（其中 ${already} 个已经是「${status}」，不会重复改动）` : '';
      this.askConfirm(
        `确定把选中的 ${ids.length} 个工位状态改为「${status}」？${tip}`,
        () => this.batchSet(status),
        '批量修改状态',
      );
    },
    batchSet(status) {
      const ids = [...this.selected];
      if (!ids.length) return;
      this.pushHistory();
      let changed = 0;
      this.stations.forEach((s) => {
        if (!ids.includes(s.id)) return;
        if (this.statusOf(s.status) === status) return; // 已经是目标状态的跳过
        s.status = status;
        changed += 1;
      });
      this.toast(
        changed ? `已将 ${changed} 个工位状态改为「${status}」` : `选中的工位已经是「${status}」`,
        changed ? 'ok' : 'warn',
      );
      this.persist();
    },
    clearSelection() {
      if (!this.selected.size) return;
      // Vue2 无法拦截 Set 的 clear()，必须整体替换引用才能触发视图更新
      this.selected = new Set();
      this.toast('已取消选择', 'ok');
    },
    batchDelete() {
      const ids = [...this.selected];
      this.askConfirm(`确定删除选中的 ${ids.length} 个工位？`, () => {
        this.removeStations(ids);
        this.toast('已删除 ' + ids.length + ' 个工位', 'ok');
      }, '批量删除');
    },
    resetAll() {
      this.askConfirm('将放弃当前所有未提交的修改，并从服务器重新加载工位列表，是否继续？', async () => {
        this.jsonVisible = false;
        await this.loadFromServer();
      }, '重置确认');
    },

    /* ---------------- 编辑弹窗 ---------------- */
    openEdit(id) {
      this.editingId = id || null;
      this.errors = { colNo: '', posNo: '' };
      const s = id ? this.stations.find(x => x.id === id) : null;
      if (s) {
        this.form = { ...this.emptyForm(), ...clone(s) };
      } else {
        // 新增：默认继承当前筛选的 processCode（若有），否则 'A'；rowNo/colNo/posNo 在现有范围内递增
        const proc = this.fProcess || (this.processOptions[0] || 'A');
        const used = new Set(this.stations.filter(x => x.processCode === proc).map(x => `${x.rowNo}|${x.colNo}|${x.posNo}`));
        let rowNo = 1, colNo = 1, posNo = 1;
        let guard = 0; // 防死循环：该区位置全被占满时也能跳出
        while (used.has(`${rowNo}|${colNo}|${posNo}`) && guard < 500) {
          guard += 1;
          posNo += 1;
          if (posNo > MAX_POS) { posNo = 1; colNo += 1; }
          if (colNo > MAX_COL) { colNo = 1; rowNo += 1; }
        }
        this.form = {
          ...this.emptyForm(),
          processCode: proc,
          rowNo, colNo, posNo,
          status: STATUS_KEYS[0], // 新增工位默认「待机」
          order: this.stations.length + 1,
        };
      }
      this.editVisible = true;
      this.$nextTick(() => {
        const el = document.querySelector('.station-admin #editTitle');
        if (el) el.focus();
      });
    },
    closeEdit() {
      this.editVisible = false;
      this.editingId = null;
    },
    // 列号 / 位号上限即时校验：输入时就提示，不用等点确定
    checkCoord() {
      const col = Number(this.form.colNo);
      const pos = Number(this.form.posNo);
      this.errors.colNo = col > MAX_COL ? `列号不得超过 ${MAX_COL}` : '';
      this.errors.posNo = pos > MAX_POS ? `位号不得超过 ${MAX_POS}` : '';
    },
    submitEdit() {
      // 名称不再手填：按「*行*列.位号」自动算出（与批量新增 / 复制同一套规则）
      const name = this.formNamePreview;
      this.errors = { colNo: '', posNo: '' };
      let bad = false;
      // 坐标必填 + 整数校验
      const rowNo = Number(this.form.rowNo) || 0;
      const colNo = Number(this.form.colNo) || 0;
      const posNo = Number(this.form.posNo) || 0;
      if (!this.form.processCode || !rowNo || !colNo || !posNo) {
        this.toast('所属区 / 行号 / 列号 / 位号 均需填写（大于 0）', 'err');
        bad = true;
      }
      // 列号 / 位号上限：超出时行内提示并拦住提交
      if (colNo > MAX_COL) { this.errors.colNo = `列号不得超过 ${MAX_COL}`; bad = true; }
      if (posNo > MAX_POS) { this.errors.posNo = `位号不得超过 ${MAX_POS}`; bad = true; }
      // 坐标在同 processCode 下唯一（允许跨区重号；编辑时不与自己冲突）
      if (!bad && this.stations.some(s =>
        s.processCode === this.form.processCode
        && s.rowNo === rowNo && s.colNo === colNo && s.posNo === posNo
        && s.id !== this.editingId
      )) {
        this.toast(`同一区下坐标 ${rowNo}/${colNo}/${posNo} 已存在`, 'err');
        bad = true;
      }
      if (bad) return;

      const data = {
        processCode: this.form.processCode,
        rowNo, colNo, posNo,
        name,
        group: this.form.group,
        type: this.form.type,
        operator: this.form.operator,
        status: this.formStatus, // 状态按有没有绑产品判断，不做手填
        remark: this.form.remark,
      };
      data.id = this.deriveId(data);
      this.pushHistory();
      if (this.editingId) {
        const s = this.stations.find(x => x.id === this.editingId);
        // id 可能因坐标变更而变，baseMap 用旧 id 还能找到；同步更新 key
        Object.assign(s, data);
        s.id = data.id;
        this.toast('已更新工位 ' + data.id, 'ok');
      } else {
        const s = Object.assign({ id: data.id }, data);
        this.stations.push(s);
        this.lastAddedId = s.id;
        this.toast('已新增工位 ' + data.id, 'ok');
      }
      const pos2 = parseInt(this.form.order, 10);
      if (!isNaN(pos2) && pos2 > 0) {
        this.moveToPosition(this.editingId || this.stations[this.stations.length - 1].id, pos2);
      }
      this.editVisible = false;
      this.editingId = null;
      // 手输了新区（如 C）也要进区全集，否则重新拉数据后下拉里又没有了
      this.collectProcess(this.stations);
      this.persist();
    },

    /* ---------------- 批量新增工位 ---------------- */
    openBatch() {
      // 区沿用当前筛选（没筛选就用第一个已知区），行列默认从 1 开始
      this.batch = {
        ...this.emptyBatch(),
        processCode: this.fProcess || (this.processOptions[0] || 'A'),
      };
      this.batchVisible = true;
    },
    closeBatch() {
      this.batchVisible = false;
    },
    submitBatch() {
      const plan = this.batchPlan;
      if (plan.msg) return this.toast(plan.msg, 'err');
      if (!plan.newCount) return this.toast('该范围内工位都已存在，没有可新增的', 'warn');

      const b = this.batch;
      this.pushHistory(); // 整批算一步，撤销一次全部回退
      let firstId = null;
      plan.items.filter(it => !it.dup).forEach(it => {
        const s = this.normalizeStation({
          station_name: it.name,        // 「一行二列.3」这类名称
          station_remark: b.remark,
          process_code: b.processCode,
          station_type: b.type,
          operator: b.operator,
          row_no: it.rowNo,
          col_no: it.colNo,
          pos_no: it.posNo,
        });
        s.order = this.stations.length + 1;
        this.stations.push(s);
        if (!firstId) firstId = s.id;
      });
      this.lastAddedId = firstId;
      this.batchVisible = false;
      this.collectProcess(this.stations);
      const last = plan.items[plan.items.length - 1];
      this.toast(
        `已批量生成 ${plan.newCount} 个工位（${plan.items[0].id} … ${last.id}）`
        + (plan.dupCount ? `，跳过已存在 ${plan.dupCount} 个` : ''),
        'ok'
      );
      this.persist();
    },

    /* ---------------- 导航 ---------------- */
    backHome() {
      // 返回主页：按权限决定（有项目管理权限→项目主页，否则→合同主页），避免跳转到无权限的项目页
      this.$router.push(UserInfoUtils.getDefaultHomeRoute());
    },

    /* ---------------- 保存 / 提交 ---------------- */
    openSaveModal() {
      if (!this.stations.length) return this.toast('工位列表为空，无可提交内容', 'warn');
      const payload = this.buildPayload();
      this.jsonText = JSON.stringify(payload, null, 2);
      this.jsonVisible = true;
    },
    buildPayload() {
      // 新接口：{ stations: [...] }，每个 station 含 processCode/rowNo/colNo/posNo/order/...，
      // 新增行可省略 id（由后端分配），已有行带 id
      return {
        stations: this.stations.map((s, i) => {
          const isNew = this.isNewStation(s);
          const row = {
            processCode: s.processCode,
            rowNo: Number(s.rowNo) || 0,
            colNo: Number(s.colNo) || 0,
            posNo: Number(s.posNo) || 0,
            order: i + 1,
            name: s.name,
            group: s.group || '',
            type: s.type || '',
            operator: s.operator || '',
            status: s.status,
            remark: s.remark || '',
          };
          if (!isNew) row.id = s.id;
          return row;
        }),
      };
    },
    async confirmSubmit() {
      const payload = this.buildPayload();
      try {
        const res = await saveStationList(payload);
        const body = res.data || {};
        if (body.code !== 200) throw new Error(body.message || '操作失败');
        this.toast('提交成功：共 ' + payload.stations.length + ' 条工位数据', 'ok');
        this.jsonVisible = false;
        this.clearDraft();
        // 重新拉取最新数据（新增行此时已由后端分配 id，刷新后基线同步）
        await this.loadFromServer();
      } catch (err) {
        this.toast('提交失败：' + (err.message || '网络错误，请重试'), 'err');
      }
    },
    async copyJson() {
      const text = this.jsonText;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
        }
        this.toast('JSON 已复制到剪贴板', 'ok');
      } catch (e) {
        this.toast('复制失败，请手动选择文本', 'err');
      }
    },

    /* ---------------- 确认框 / Toast ---------------- */
    askConfirm(text, fn, title) {
      this.confirmText = text;
      this.confirmTitle = title || '请确认';
      this.confirmFn = fn;
      this.confirmVisible = true;
    },
    runConfirm() {
      this.confirmVisible = false;
      if (this.confirmFn) { const fn = this.confirmFn; this.confirmFn = null; fn(); }
    },
    toast(msg, type) {
      const id = ++this.toastId;
      this.toasts.push({ id, msg, type: type || '' });
      setTimeout(() => { this.toasts = this.toasts.filter(t => t.id !== id); }, 2500);
    },

    /* ---------------- 定位行 ---------------- */
    locateRow(id) {
      // 先清除筛选以便定位
      this.kw = ''; this.fGroup = ''; this.fStatus = '';
      this.$nextTick(() => {
        const row = document.querySelector(`.station-admin .row[data-id="${id}"]`);
        if (!row) return;
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        row.classList.remove('flash');
        void row.offsetWidth;
        row.classList.add('flash');
      });
    },

    /* ---------------- 工位总览面板 ---------------- */
    toggleMap() { this.showMap = !this.showMap; },
    switchZone(z) { this.mapZone = z; },
    // 点总览里的工位：关掉面板再定位，避免面板挡住列表高亮行
    locateFromMap(id) {
      this.showMap = false;
      this.locateRow(id);
    },
    // 总览色块的悬浮提示：批量模式下说明是勾选还是取消勾选
    slotTitle(st) {
      const base = `${st.id} · 位号 ${st.posNo} · ${st.name || '未命名'} · ${this.statusText(st.status)}${st.operator ? ' · ' + st.operator : ''}`;
      return `${base}（${this.bulkMode ? (this.selected.has(st.id) ? '点击取消勾选' : '点击勾选') : '点击定位到列表'}）`;
    },
    // 点总览色块：批量模式下勾 / 取消勾选（面板保持打开，方便连着选），否则照旧定位到列表
    onMapSlotClick(st) {
      if (!this.bulkMode) return this.locateFromMap(st.id);
      this.togglePick(st.id, !this.selected.has(st.id));
    },
    // 勾选 / 取消勾选单个工位：整体替换 Set 引用，Vue2 才能感知变化
    togglePick(id, on) {
      const next = new Set(this.selected);
      if (on) next.add(id); else next.delete(id);
      this.selected = next;
    },
    // 批量模式下「选中本区 / 取消本区」：按当前总览所在区一次性勾 / 取消该区全部工位
    pickZone(on) {
      if (!this.overviewZone) return;
      const ids = this.stations
        .filter(s => (s.processCode || '未分区') === this.overviewZone)
        .map(s => s.id);
      if (!ids.length) return;
      const next = new Set(this.selected);
      ids.forEach(id => (on ? next.add(id) : next.delete(id)));
      this.selected = next;
      this.toast(`已${on ? '选中' : '取消选中'} ${this.overviewZone} 区 ${ids.length} 个工位`, 'ok');
    },
    // 点击面板外部关闭
    onDocClick(e) {
      if (!this.showMap) return;
      const root = this.$refs.mapSel;
      if (root && !root.contains(e.target)) this.showMap = false;
    },

    /* ---------------- 快捷键 ---------------- */
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.editVisible = false;
        this.batchVisible = false;
        this.jsonVisible = false;
        this.confirmVisible = false;
        this.showMap = false;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        e.preventDefault();
        this.undo();
      }
    },
  },
};
</script>

<style scoped>
/* ================= 白色主题 ================= */
/* 注意：组件使用 scoped 样式，CSS 变量必须定义在组件根元素 .station-admin 上
   （若定义在 :root 上，scoped 编译后不生效，导致 var(--xx) 全部失效、布局错乱） */
.station-admin {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  color: var(--txt);
  font: 14px/1.5 "Microsoft YaHei", "PingFang SC", "Segoe UI", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  --bg: #ffffff;
  --bg-soft: #f6f8fb;
  --panel: #ffffff;
  --panel-2: #f6f8fb;
  --line: #e5e9f0;
  --line-strong: #cbd5e1;
  --txt: #1e293b;
  --txt-dim: #475569;
  --txt-mute: #94a3b8;
  --accent: #0284c7;
  --accent-d: #0369a1;
  --accent-soft: rgba(2, 132, 199, .06);
  --ok: #059669;
  --warn: #d97706;
  --err: #dc2626;
  --radius: 14px;
  --shadow: 0 10px 30px rgba(15, 23, 42, .08);
  --input-bg: #ffffff;
  --head-bg: #f8fafc;
  --code-bg: #f8fafc;
  --mask: rgba(15, 23, 42, .4);
  --appbar-bg: rgba(255, 255, 255, .92);
  /* 序号|编号|所属区|工位名称|工位类型|负责人|状态|备注|操作 */
  --cols: 56px minmax(110px, 1.1fr) 68px minmax(160px, 1.8fr)
          minmax(88px, 1fr) minmax(96px, 1fr) 96px
          minmax(110px, 1.2fr) 152px;
}
.station-admin * { box-sizing: border-box; }
.station-admin h1, .station-admin h2, .station-admin h3 { margin: 0; font-weight: 600; }
.station-admin button { font-family: inherit; }

/* ---------------- 顶栏 ---------------- */
.app-bar {
  flex: none;
  z-index: 40;
  background: var(--appbar-bg);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--line);
}
.bar-inner {
  /* 不再居中限宽：品牌与操作区分别贴左右两端（内边距与下方 .page 对齐） */
  max-width: none;
  margin: 0;
  padding: 12px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.brand { display: flex; align-items: center; gap: 12px; min-width: 0; margin-right: auto; }
.logo { width: 40px; height: 40px; flex: none; }
.logo svg { width: 100%; height: 100%; display: block; }
.brand-txt h1 { font-size: 19px; letter-spacing: .5px; }
.brand-txt p { margin: 2px 0 0; font-size: 11px; color: var(--txt-mute); letter-spacing: 1.4px; }
.bar-actions {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  /* margin-left:auto：窄屏换行后仍整体靠右，不会被拉到左侧 */
  margin-left: auto; justify-content: flex-end;
}
/* 未保存提示：橙底 + 闪烁圆点 + 呼吸光圈，务必一眼看见 */
.dirty-flag {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 13px; border-radius: 999px;
  font-size: 12.5px; font-weight: 700; letter-spacing: .3px;
  color: #7c2d12; background: #fff7ed;
  border: 1px solid #f97316;
  cursor: default; user-select: none;
  animation: dirtyPulse 1.5s ease-in-out infinite;
}
.dirty-flag .df-dot { width: 8px; height: 8px; border-radius: 50%; background: #f97316; animation: dirtyBlink 1s steps(1, end) infinite; }
.dirty-flag .df-txt { white-space: nowrap; }
@keyframes dirtyPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, .45); }
  50% { box-shadow: 0 0 0 7px rgba(249, 115, 22, 0); }
}
@keyframes dirtyBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: .25; } }
/* 有未保存改动时，保存按钮一起脉动 */
.btn.save.need-save { animation: saveGlow 1.5s ease-in-out infinite; }
@keyframes saveGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, .55); }
  50% { box-shadow: 0 0 0 7px rgba(16, 185, 129, 0); }
}

/* ---------------- 按钮 ---------------- */
/* 按钮统一间距：右侧、下方各 1px，避免相邻按钮 / 色块贴在一起 */
button { margin-right: 1px; margin-bottom: 1px; }

.btn {
  border: 1px solid var(--line-strong);
  background: var(--panel-2);
  color: var(--txt);
  padding: 9px 16px;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
  transition: background .15s, border-color .15s, color .15s;
  white-space: nowrap;
}
.btn:hover { border-color: var(--accent); color: var(--accent); }
.btn.primary { background: var(--accent); border-color: var(--accent); color: #fff; font-weight: 600; }
.btn.primary:hover { background: var(--accent-d); border-color: var(--accent-d); color: #fff; }
.btn.save { background: #10b981; border-color: #10b981; color: #fff; font-weight: 600; }
.btn.save:hover { background: #059669; border-color: #059669; color: #fff; }
.btn.danger { background: #fff; border-color: rgba(220, 38, 38, .35); color: var(--err); }
.btn.danger:hover { background: rgba(220, 38, 38, .06); border-color: var(--err); color: var(--err); }
.btn.ghost { background: #fff; }
.backhome {
  /* 仅小房子图标：正方形图标按钮，与相邻按钮同高 */
  display: inline-grid;
  place-items: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border-radius: 10px;
  color: var(--accent);
  border-color: rgba(2, 132, 199, .35);
  background: rgba(2, 132, 199, .06);
}
.backhome:hover { color: #fff; border-color: var(--accent); background: var(--accent); }
.backhome svg { width: 17px; height: 17px; display: block; }
.btn.sm { padding: 6px 11px; font-size: 12px; border-radius: 8px; }
.icon-btn {
  width: 30px; height: 30px;
  display: grid; place-items: center;
  border: 1px solid var(--line);
  background: transparent;
  color: var(--txt-dim);
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
}
.icon-btn:hover { color: var(--accent); border-color: var(--accent); background: var(--accent-soft); }

/* ---------------- 页面容器 ---------------- */
.page {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 2560px;
  margin: 0 auto;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
}
.panel {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: 0 1px 2px rgba(15, 23, 42, .03);
}

/* ---------------- 位置预览 ---------------- */
.pos-panel { padding: 12px 16px 14px; }
.pos-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
.pos-head h3 { font-size: 13px; color: var(--txt-dim); letter-spacing: .5px; }
.pos-legend { display: flex; gap: 8px; }
.pos-legend .lg { font-size: 11px; padding: 2px 9px; border-radius: 999px; border: 1px solid; }
/* ---------------- 五状态配色（徽章 / 位置预览芯片 / 图例 共用） ----------------
   空闲绿 · 生产中蓝 · 故障红 · 维修中橙 · 离线深灰
   （.st-paused 的样式保留，仅用于历史草稿里残留的旧状态）
   （总览里的工位「色块」用同色系的实心填充，见 .mp-slot.st-*） */
.badge.st-idle, .pos-chip.st-idle, .pos-legend .lg.st-idle, .mp-foot i.st-idle { color: #15803d; border-color: rgba(34, 197, 94, .4); background: rgba(34, 197, 94, .12); }
.badge.st-running, .pos-chip.st-running, .pos-legend .lg.st-running, .mp-foot i.st-running { color: #1d4ed8; border-color: rgba(59, 130, 246, .4); background: rgba(59, 130, 246, .12); }
.badge.st-paused, .pos-chip.st-paused, .pos-legend .lg.st-paused, .mp-foot i.st-paused { color: #a16207; border-color: rgba(250, 204, 21, .55); background: rgba(250, 204, 21, .18); }
.badge.st-fault, .pos-chip.st-fault, .pos-legend .lg.st-fault, .mp-foot i.st-fault { color: #b91c1c; border-color: rgba(239, 68, 68, .4); background: rgba(239, 68, 68, .12); }
.badge.st-maintenance, .pos-chip.st-maintenance, .pos-legend .lg.st-maintenance, .mp-foot i.st-maintenance { color: #c2410c; border-color: rgba(249, 115, 22, .4); background: rgba(249, 115, 22, .12); }
.badge.st-offline, .pos-chip.st-offline, .pos-legend .lg.st-offline, .mp-foot i.st-offline { color: #334155; border-color: rgba(71, 85, 105, .45); background: rgba(71, 85, 105, .14); }
.badge.st-unknown, .pos-chip.st-unknown, .pos-legend .lg.st-unknown, .mp-foot i.st-unknown { color: var(--txt-mute); border-color: rgba(148, 163, 184, .4); background: rgba(148, 163, 184, .12); }
.pos-map {
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* 位置预览最大高度，超出滚动（数值可按需调整） */
  max-height: 110px;
  overflow-y: auto;
}
/* flex 列容器会把子项压缩进限高内而不溢出，必须禁止压缩才能触发滚动条 */
.pos-map > * { flex: none; }
.pos-empty { padding: 18px 0; text-align: center; color: var(--txt-mute); font-size: 13px; }
.pos-group { display: flex; align-items: flex-start; gap: 12px; }
.pos-gname { flex: none; width: 66px; padding-top: 5px; font-size: 12px; font-weight: 600; color: var(--txt-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pos-gname i { font-style: normal; font-weight: 400; color: var(--txt-mute); margin-left: 4px; }
.pos-chips { display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; }
.pos-chip {
  min-width: 54px; text-align: center; padding: 4px 9px;
  border-radius: 8px; border: 1px solid var(--line-strong);
  background: var(--panel-2); color: var(--txt-dim);
  font-family: Consolas, Menlo, monospace; font-size: 12px;
  cursor: pointer; transition: border-color .15s, background .15s, color .15s;
}
.pos-chip:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-soft); }

/* 批量新增工位：编号预览区 */
.batch-chips {
  display: flex; flex-wrap: wrap; gap: 6px;
  max-height: 132px; overflow-y: auto;
  padding: 8px; border: 1px solid var(--line); border-radius: 8px; background: var(--code-bg);
}
.batch-chips .pos-chip { cursor: default; }
/* 已存在的坐标：标红并划掉，提示会被跳过 */
.batch-chips .pos-chip.dup {
  color: var(--err); border-color: rgba(220, 38, 38, .45);
  background: rgba(220, 38, 38, .1); text-decoration: line-through; opacity: .75;
}
.batch-chips .pos-chip.dup:hover { border-color: var(--err); background: rgba(220, 38, 38, .14); }
.pos-more { align-self: center; font-size: 11.5px; color: var(--txt-mute); }

/* ---------------- 工位总览（右上角展开面板） ---------------- */
.map-sel { position: relative; flex: none; }
.map-btn { display: inline-flex; align-items: center; gap: 6px; }
.map-btn svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 1.7; }
.map-sel.open .map-btn { border-color: var(--accent); color: var(--accent); background: var(--accent-soft); }

.map-panel {
  position: absolute; top: calc(100% + 10px); right: 0; z-index: 60;
  width: 340px; max-width: calc(100vw - 40px);
  padding: 12px 12px 10px;
  background: var(--panel);
  border: 1px solid var(--line-strong);
  border-radius: 12px;
  box-shadow: var(--shadow);
  animation: mpIn .14s ease-out;
}
@keyframes mpIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
.mp-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
.mp-title { font-size: 13px; font-weight: 600; white-space: nowrap; }
.mp-no { font-size: 11px; color: var(--txt-mute); white-space: nowrap; }

/* 区切换 */
.mp-tabs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.mp-tab {
  padding: 3px 10px;
  font-size: 11.5px; color: var(--txt-dim);
  background: var(--panel-2); border: 1px solid var(--line); border-radius: 999px;
  cursor: pointer; transition: color .15s, border-color .15s, background .15s;
}
.mp-tab:hover { color: var(--accent); border-color: var(--accent); }
.mp-tab.on { color: #fff; font-weight: 600; background: var(--accent); border-color: var(--accent); }
.mp-empty { padding: 14px 0; text-align: center; font-size: 12px; color: var(--txt-mute); }

/* 行列矩阵：行数 / 列数由该区最大行号 / 最大列号决定 */
.mp-body { display: flex; flex-direction: column; gap: 4px; max-height: 300px; overflow: auto; }
.mp-row { display: flex; align-items: stretch; gap: 4px; }
.mp-rno { flex: none; width: 16px; text-align: center; font-size: 10px; color: var(--txt-mute); line-height: 22px; }
.mp-cno { flex: 1 1 0; min-width: 0; text-align: center; font-size: 10px; color: var(--txt-mute); }
.mp-colhead .mp-cno, .mp-colhead .mp-rno { line-height: 12px; }

.mp-cell {
  flex: 1 1 0; min-width: 0;
  height: 22px; padding: 2px;
  /* 间距交给色块自身的 margin（右 / 下各 1px），这里不再用 gap */
  display: flex; gap: 0;
  border: 1px solid var(--line-strong); border-radius: 5px;
  background: var(--panel-2);
}
/* 该坐标没有工位：虚线空格，保证整体行列位置仍然对得上 */
.mp-cell.is-null { border-style: dashed; border-color: var(--line); background: transparent; }

/* 位号：一个工位 = 一个等分矩形色块（不显示数字），颜色代表状态
   格子内按实际工位数等分（6 个工位 → 6 等分，2 个 → 2 等分） */
.mp-slot {
  /* 不写死高度：由父格 stretch 撑满，配合 margin-bottom 1px 不会溢出格子 */
  flex: 1 1 0; min-width: 0;
  display: block;
  padding: 0; border: 1px solid transparent; border-radius: 3px;
  background: #cbd5e1; cursor: pointer;
  transition: transform .12s, box-shadow .12s, background .15s;
}
.mp-slot.is-null {
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 10.5px; line-height: 1;
  color: var(--txt-mute); background: transparent; cursor: default;
}
.mp-slot:not(.is-null):hover { transform: scaleY(1.22); box-shadow: 0 0 0 2px rgba(2, 132, 199, .5); }
/* 批量模式下被勾选的工位：加一圈描边 + 轻微放大，和列表里的勾选状态对上 */
.mp-slot.picked {
  box-shadow: 0 0 0 2px var(--accent), 0 0 0 4px rgba(2, 132, 199, .22);
  transform: scaleY(1.18);
}
/* 批量模式提示条：说明点色块可勾选，并给「选中本区 / 取消本区」两个快捷操作 */
.mp-pickbar {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  margin-bottom: 8px; padding: 5px 8px;
  border: 1px dashed rgba(2, 132, 199, .45); border-radius: 8px;
  background: var(--accent-soft);
  font-size: 11px; color: var(--txt-mute);
}
.mp-pick-btn {
  padding: 2px 8px; border-radius: 999px;
  border: 1px solid var(--line-strong); background: #fff; color: var(--txt);
  font-size: 11px; cursor: pointer;
}
.mp-pick-btn:hover { border-color: var(--accent); color: var(--accent); }
/* 五状态填充色：空闲绿 / 生产中蓝 / 故障红 / 维修中橙 / 离线深灰 */
.mp-slot.st-idle { background: #22c55e; border-color: #15803d; }
.mp-slot.st-running { background: #3b82f6; border-color: #1d4ed8; }
.mp-slot.st-paused { background: #facc15; border-color: #ca8a04; }
.mp-slot.st-fault { background: #ef4444; border-color: #b91c1c; }
.mp-slot.st-maintenance { background: #f97316; border-color: #c2410c; }
.mp-slot.st-offline { background: #475569; border-color: #334155; }
.mp-slot.st-unknown { background: #cbd5e1; border-color: #94a3b8; }

.mp-foot { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.mp-foot i {
  display: inline-flex; align-items: center; gap: 4px;
  font-style: normal; font-size: 10.5px; padding: 1px 8px; border-radius: 999px; border: 1px solid;
}
/* 图例小色块：与总览里的工位色块同色 */
.mp-foot i .lg-sw { width: 10px; height: 8px; border-radius: 2px; background: currentColor; opacity: .85; }

/* ---------------- 工具栏 ---------------- */
.toolbar { padding: 12px 14px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.tools-left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex: 1 1 420px; }
.tools-right { display: flex; align-items: center; gap: 10px; }
.tip { font-size: 12px; color: var(--txt-mute); }
.field.search {
  position: relative;
  display: flex; align-items: center;
  flex: 1 1 260px;
  max-width: 380px;
  background: var(--input-bg);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 0 10px;
}
.field.search .ic { width: 16px; height: 16px; color: var(--txt-mute); flex: none; }
.field.search input { flex: 1; background: transparent; border: 0; outline: none; color: var(--txt); padding: 9px 8px; font-size: 13px; min-width: 0; }
.field.search input::placeholder { color: var(--txt-mute); }
.select, .fi input, .fi select, .fi textarea {
  background: var(--input-bg);
  border: 1px solid var(--line);
  color: var(--txt);
  border-radius: 10px;
  padding: 9px 10px;
  font-size: 13px;
  outline: none;
  width: 100%;
}
.select { width: auto; min-width: 118px; cursor: pointer; }
.select:focus, .fi input:focus, .fi select:focus, .fi textarea:focus { border-color: var(--accent); }
.select option { background: #fff; color: var(--txt); }

/* ---------------- 批量条 ---------------- */
.batch-bar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 10px 14px;
  border: 1px solid rgba(56, 189, 248, .35);
  background: rgba(56, 189, 248, .09);
  border-radius: var(--radius);
  font-size: 13px;
}
.batch-bar b { color: var(--accent); }
.batch-acts { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.batch-acts .ba-label { font-size: 12.5px; color: var(--txt-mute); }
.batch-acts .select { min-width: 104px; }
/* 批量改「负责人 / 备注」的输入框：和 .select 同一套观感 */
.batch-acts .ba-input {
  height: 32px; padding: 0 9px; min-width: 104px;
  border: 1px solid var(--line); border-radius: 8px;
  background: var(--input-bg); color: var(--txt);
  font-size: 12.5px; font-family: inherit; outline: none;
}
.batch-acts .ba-input::placeholder { color: var(--txt-mute); }
.batch-acts .ba-input:focus { border-color: var(--accent); outline: none; }
.batch-acts .ba-input:disabled { opacity: .45; cursor: not-allowed; }
.batch-acts .ba-remark { min-width: 150px; }
/* 「清空」勾选：紧跟在对应输入框右侧 */
.batch-acts .ba-clr {
  display: inline-flex; align-items: center; gap: 3px; margin-left: -2px;
  font-size: 12px; color: var(--txt-mute); cursor: pointer; user-select: none;
}
.batch-acts .ba-clr input { width: 13px; height: 13px; margin: 0; accent-color: var(--accent); cursor: pointer; }
/* 批量改状态 与 批量删除 之间的竖分隔线 */
.batch-acts .ba-sep { width: 1px; height: 18px; background: var(--line-strong); margin: 0 2px; }
.btn:disabled { opacity: .45; cursor: not-allowed; }

/* ---------------- 列表 ---------------- */
.list-panel {
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.list-head, .row { display: grid; gap: 10px; align-items: center; padding: 0 14px; }
.list-head {
  grid-template-columns: var(--cols);
  padding: 11px 14px;
  font-size: 12px;
  color: var(--txt-dim);
  letter-spacing: .5px;
  border-bottom: 1px solid var(--line);
  background: var(--head-bg);
  /* 表头与行同处滚动容器，滚动时固定顶部，确保与各行逐列对齐 */
  position: sticky;
  top: 0;
  z-index: 3;
}
.list-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.row {
  grid-template-columns: var(--cols);
  border-bottom: 1px solid var(--line);
  padding: 9px 14px;
  transition: background .15s;
}
.row:hover { background: var(--accent-soft); }
.row.flash { animation: flash .9s ease; }
@keyframes flash { from { background: rgba(2, 132, 199, .16); } to { background: transparent; } }
.cell { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cell.c-idx { display: flex; align-items: center; gap: 6px; color: var(--txt-dim); }
.cell.c-idx input[type="checkbox"],
.list-head .c-idx input[type="checkbox"] { width: 15px; height: 15px; margin: 0; cursor: pointer; accent-color: var(--accent); }
/* 编号列：列头与单元格都用 flex 靠左，避免模板缩进的前导空格让两者错位 */
.cell.c-code {
  display: flex; align-items: center; justify-content: flex-start;
  font-family: Consolas, Menlo, monospace; color: var(--accent);
}
/* 所属区的字体与工位名称保持一致 */
.cell.c-proc, .cell.c-name { color: var(--txt); }
/* 编号列：点击表头排序 */
.list-head .cell.sortable { display: flex; align-items: center; gap: 4px; cursor: pointer; user-select: none; }
.list-head .cell.sortable:hover { color: var(--accent); }
.list-head .cell.sortable.sorted { color: var(--accent); }
.sort-ind { font-style: normal; font-size: 10px; opacity: .45; }
.list-head .cell.sortable.sorted .sort-ind { opacity: 1; }
.row.picked { background: rgba(2, 132, 199, .07); }
.cell.c-type, .cell.c-operator { color: var(--txt-dim); }
.cell.c-actions { display: flex; gap: 6px; justify-content: flex-start; }
.idx { min-width: 24px; height: 22px; display: inline-grid; place-items: center; border-radius: 6px; background: var(--head-bg); border: 1px solid var(--line); font-size: 12px; }
.act-btn {
  width: 28px; height: 28px;
  display: grid; place-items: center;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--txt-dim);
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  padding: 0;
  transition: .15s;
}
.act-btn:hover { color: #fff; border-color: var(--accent); background: var(--accent); }
.act-btn.del:hover { color: #fff; border-color: var(--err); background: var(--err); }
.act-btn[disabled] { opacity: .35; cursor: not-allowed; }
.act-btn[disabled]:hover { color: var(--txt-dim); border-color: var(--line); background: var(--panel); }
/* 状态徽章：具体配色见上方「六状态配色」，这里只给未知状态兜底，避免徽章没有底色 */
.badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; border: 1px solid transparent; color: var(--txt-dim); background: rgba(148, 163, 184, .12); border-color: rgba(148, 163, 184, .3); }
.tag-new { display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 5px; font-size: 11px; font-weight: 600; line-height: 1.4; vertical-align: middle; color: #fff; background: var(--accent); }
.empty { padding: 44px 16px; text-align: center; color: var(--txt-mute); font-size: 13px; }
/* 备注列：与工位类型、负责人等普通列保持同一套格式 */
.cell.c-remark { color: var(--txt-dim); }
.foot-tip { margin: 4px 2px 0; font-size: 12px; color: var(--txt-mute); line-height: 1.7; }

/* ---------------- 弹窗 ---------------- */
.modal { position: fixed; inset: 0; z-index: 90; display: grid; place-items: center; padding: 20px; }
.mask { position: absolute; inset: 0; background: var(--mask); backdrop-filter: blur(3px); }
.dialog { position: relative; width: min(680px, 100%); max-height: 88vh; display: flex; flex-direction: column; background: var(--panel); border: 1px solid var(--line-strong); border-radius: 16px; box-shadow: var(--shadow); animation: pop .18s ease; }
.dialog.wide { width: min(880px, 100%); }
.dialog.narrow { width: min(420px, 100%); }
@keyframes pop { from { transform: translateY(10px) scale(.98); opacity: 0; } }
.dlg-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid var(--line); }
.dlg-head h3 { font-size: 16px; }
.dlg-body { padding: 18px; overflow: auto; }
.dlg-foot { padding: 12px 18px; border-top: 1px solid var(--line); display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.fi { display: flex; flex-direction: column; gap: 6px; font-size: 12px; color: var(--txt-dim); }
.fi.full { grid-column: 1 / -1; }
.fi textarea { resize: vertical; min-height: 62px; font-family: inherit; }
.fi.required > span::after { content: " *"; color: var(--err); }
.fi .err { font-style: normal; color: var(--err); font-size: 11px; min-height: 14px; }
.fi.invalid input { border-color: var(--err); }
/* 自动算出的工位编号：只读展示，坐标已存在时标红 */
.fi.readonly { gap: 4px; }
.id-preview {
  display: flex; align-items: center; min-height: 34px; padding: 0 10px;
  border: 1px dashed var(--line-strong); border-radius: 8px; background: var(--code-bg);
  font-family: Consolas, Menlo, monospace; font-size: 13.5px; letter-spacing: .6px;
  color: var(--accent); width: 100%;
}
.fi .tip { font-style: normal; font-size: 11px; color: var(--txt-mute); min-height: 14px; }
.fi.readonly.dup .id-preview { color: var(--err); border-color: var(--err); border-style: solid; }
.fi.readonly.dup .tip { color: var(--err); }
.json-view { margin: 0; padding: 14px; background: var(--code-bg); border: 1px solid var(--line); border-radius: 12px; font-family: Consolas, Menlo, monospace; font-size: 12.5px; line-height: 1.65; color: var(--txt); max-height: 46vh; overflow: auto; white-space: pre; }
.diff { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.diff span { font-size: 12px; padding: 4px 10px; border-radius: 999px; border: 1px solid var(--line-strong); color: var(--txt-dim); }
.diff span.add { color: var(--ok); border-color: rgba(52, 211, 153, .4); background: rgba(52, 211, 153, .1); }
.diff span.upd { color: var(--accent); border-color: rgba(56, 189, 248, .4); background: rgba(56, 189, 248, .1); }
.diff span.del { color: var(--err); border-color: rgba(251, 113, 133, .4); background: rgba(251, 113, 133, .1); }
.diff span.mov { color: var(--warn); border-color: rgba(251, 191, 36, .4); background: rgba(251, 191, 36, .1); }
.hint { margin: 12px 0 0; font-size: 12px; color: var(--txt-mute); }
.cf-text { margin: 0; font-size: 14px; color: var(--txt-dim); line-height: 1.7; }

/* ---------------- Toast ---------------- */
.toast-wrap { position: fixed; z-index: 120; right: 18px; bottom: 18px; display: flex; flex-direction: column; gap: 10px; }
.toast { padding: 10px 16px; border-radius: 10px; background: var(--panel-2); border: 1px solid var(--line-strong); color: var(--txt); font-size: 13px; box-shadow: var(--shadow); animation: slide .2s ease; }
.toast.ok { border-color: rgba(52, 211, 153, .5); }
.toast.warn { border-color: rgba(251, 191, 36, .5); }
.toast.err { border-color: rgba(251, 113, 133, .5); }
@keyframes slide { from { transform: translateX(20px); opacity: 0; } }

/* ---------------- 响应式 ---------------- */
@media (max-width: 1279px) {
  .station-admin { --cols: 20px minmax(96px, 1.05fr) 56px minmax(150px, 1.7fr) minmax(80px, 1fr) minmax(88px, 1.05fr) 92px minmax(100px, 1.1fr) 132px; }
}
@media (max-width: 1023px) {
  .pos-gname { width: 58px; }
}
/* ≤859px：隐藏表头，行变为卡片式「标签: 值」布局 */
/* @media (max-width: 859px) {
  .list-head { display: none; }
  .list-panel { background: transparent; border: 0; }
  .list-body { gap: 10px; }
  .row {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 14px;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    box-shadow: 0 1px 2px rgba(15, 23, 42, .03);
  }
  .row .cell {
    display: grid;
    grid-template-columns: 78px 1fr;
    gap: 10px;
    align-items: center;
    white-space: normal;
  }
  .row .cell::before {
    content: attr(data-label);
    color: var(--txt-mute);
    font-size: 12px;
  }
  .row .c-idx::before { content: "序号"; }
  .row .c-actions { grid-template-columns: 1fr; justify-content: stretch; }
  .row .c-actions::before { content: "操作"; }
  .act-btn { width: 34px; height: 32px; }
  .pos-group { flex-direction: column; gap: 6px; }
  .pos-gname { width: auto; padding-top: 0; }
  .bar-inner { padding: 10px 14px; }
  .brand-txt p { display: none; }
  .bar-actions { width: 100%; }
  .bar-actions .btn { flex: 1 1 auto; }
  .page { padding: 14px 14px 40px; }
  .form-grid { grid-template-columns: 1fr; }
  .tools-left { flex: 1 1 100%; }
  .field.search { max-width: none; }
  .toast-wrap { left: 14px; right: 14px; }
} */
@media (max-width: 480px) {
  .pos-chip { min-width: 48px; padding: 3px 7px; font-size: 11px; }
  .pos-legend { display: none; }
}
</style>
